import React, { useCallback, useEffect, useState } from 'react';
import { MonitorHead, StatusBadge } from '../../components';
import { HubSyncNavIcon } from '../../components/navIcons';
import { apiService } from '../../services';
import type { HubSkillsSyncLogRow, PaginationInfo } from '../../types';
import { formatDurationClock, formatRunStarted } from '../../utils/monitorFormat';
import { failureCountTone, hubSyncStatus } from '../../utils/monitorStatus';
import '../monitoring/monitoring.css';

const PAGE_SIZE = 20;

export const HubSyncLogs: React.FC = () => {
  const [runs, setRuns] = useState<HubSkillsSyncLogRow[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async (nextPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getHubSkillsSyncLogs(nextPage, PAGE_SIZE);
      if (response.success) {
        setRuns(response.data.runs);
        setPagination(response.data.pagination);
        setPage(response.data.pagination.page);
      } else {
        setError(response.message || 'Failed to load Hub sync logs');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Hub sync logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLogs(page);
  }, [fetchLogs, page]);

  const latest = runs[0];
  const latestStatus = latest ? hubSyncStatus(latest) : null;

  return (
    <div className="monitor-page">
      <main className="monitor-page__content">
        <MonitorHead
          title="Hub sync"
          icon={<HubSyncNavIcon size={20} />}
          backTo="/pipeline"
          backLabel="← Pipeline"
          subtitle="Each run that pulls skills from the Hub. Run log only: skill quality and people are not shown here."
          aside={
            latestStatus
              ? <span className="monitor-updated">Latest run {latestStatus.label.toLowerCase()}</span>
              : undefined
          }
        />

        {error && (
          <div className="monitor-error">
            <p>{error}</p>
            <button type="button" className="pa-btn" onClick={() => void fetchLogs(page)}>
              Retry
            </button>
          </div>
        )}

        {!error && loading && runs.length === 0 && (
          <p className="monitor-empty">Loading Hub sync runs…</p>
        )}
        {!error && !loading && runs.length === 0 && (
          <p className="monitor-empty">No Hub skills sync runs yet.</p>
        )}

        {runs.length > 0 && (
          <div className="monitor-card">
            <div className="monitor-table-wrap">
              <table className="monitor-table" style={{ minWidth: 720 }}>
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Status</th>
                    <th className="r">Duration</th>
                    <th className="r">Items synced</th>
                    <th className="r">Failures</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((row) => {
                    const status = hubSyncStatus(row);
                    const failTone = failureCountTone(row.download_failed_count, row);
                    return (
                      <tr key={row.id}>
                        <td className="nowrap">{formatRunStarted(row.started_at)}</td>
                        <td>
                          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                        </td>
                        <td className="r num">{formatDurationClock(row.duration_ms)}</td>
                        <td className="r num">{row.scores_written_count}</td>
                        <td className={`r num${failTone !== 'none' ? ` fail-count--${failTone}` : ''}`}>
                          {row.download_failed_count}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pagination && pagination.total_pages > 1 && (
              <div className="monitor-foot" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <span style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="pa-btn"
                    disabled={!pagination.has_prev || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="pa-btn"
                    disabled={!pagination.has_next || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </span>
              </div>
            )}
          </div>
        )}
        <p className="monitor-note">Failures counts items that did not sync in that run.</p>
      </main>
    </div>
  );
};

export default HubSyncLogs;
